/**
 * **What the right column says about one session** — the T-A5 panel: a status badge, who, what,
 * when, and the deadline the client still has to call it off for free.
 *
 * The badge vocabulary is the mobile session sheet's (`calendar.sheet.badge.*`, read through
 * `sheetStatus`'s table): a consultation past its start is `Do potwierdzenia`, never missed, and a
 * running session names the hour it started. Actions are not decided here yet — the panel's action
 * rows arrive with the writes that stand behind them.
 *
 * Pure: no React, no DOM, no query hook, no i18next. Words are i18n KEYS; the component translates.
 */

import { occurrenceKindKey, occurrenceKindWordKey } from '~/entities/calendar-event';
import { parseInstant } from '~/shared/lib/calendarDate';

import { awaitsVerdict, isLiveAt, type BookingSession } from './bookingSession';
import type { PillTone } from './dayAgenda';

export interface DetailBadge {
  readonly tone: PillTone;
  readonly labelKey: string;
  readonly params?: Readonly<Record<string, string>>;
}

export interface SessionDetail {
  readonly badge: DetailBadge;
  /** `Trening`, `Trening z planu`, `Konsultacja`. */
  readonly kindKey: string;
  /** `na miejscu`, `zdalnie`, `online`. */
  readonly placeKey: string;
  readonly clientBooked: boolean;
  /**
   * The moment the client may cancel for free until, while that is still a question — a session
   * that has not happened and has not been settled. `passed` says the moment is behind us, which
   * the panel states in words rather than hiding: the trainer asking is usually asking because it
   * has passed.
   */
  readonly cancelFree: { readonly ms: number; readonly passed: boolean } | null;
  /**
   * A LATE cancellation: the moment the client could still have cancelled for free, which the
   * booking froze when it was made (R12) — the fact the charge rests on. `null` for anything else,
   * or when the wire carries no moment. When the client DID cancel is not on the wire.
   */
  readonly lateCancelFreeUntil: number | null;
}

function badgeOf(session: BookingSession, nowMs: number): DetailBadge {
  if (isLiveAt(session, nowMs)) {
    return {
      tone: 'accent',
      labelKey: 'calendar.sheet.badge.inProgress',
      params: { time: session.startClock ?? '' },
    };
  }
  switch (session.status) {
    case 'completed':
      return { tone: 'good', labelKey: 'calendar.sheet.badge.completed' };
    case 'missed':
      return { tone: 'warn', labelKey: 'calendar.status.missed' };
    case 'noShow':
      return { tone: 'danger', labelKey: 'calendar.sheet.badge.noShow' };
    case 'cancelled':
      return { tone: 'danger', labelKey: 'calendar.sheet.badge.cancelled' };
    case 'lateCancelled':
      return { tone: 'warn', labelKey: 'calendar.status.lateCancelled' };
    case 'skipped':
      return { tone: 'neutral', labelKey: 'calendar.sheet.badge.skipped' };
    case 'emptySlot':
      return { tone: 'neutral', labelKey: 'calendar.sheet.badge.emptySlot' };
    case null:
      // A word this build has never heard: said as unknown, never folded onto "scheduled".
      return { tone: 'neutral', labelKey: 'web:booking.detail.unknownStatus' };
    case 'live':
    case 'scheduled':
      break;
  }
  if (awaitsVerdict(session, nowMs)) {
    return { tone: 'warn', labelKey: 'calendar.sheet.badge.consultAwaiting' };
  }
  return {
    tone: 'accent',
    // A video consultation is not held on site, whatever `isRemote` says (the same reading as `placeKeyOf`).
    labelKey: session.isRemote || (session.kind === 'consultation' && session.isVideoCall)
      ? 'calendar.sheet.badge.scheduledRemote'
      : 'calendar.sheet.badge.scheduledStationary',
  };
}

/** `Trening` or `Konsultacja` — the kind in one word, as the grid and the agenda write it. */
export function kindWordKey(session: BookingSession): string {
  return occurrenceKindWordKey(session.kind);
}

function kindKeyOf(session: BookingSession): string {
  return occurrenceKindKey({ kind: session.kind, planSessionName: session.sessionName });
}

/** Where the session is held: on site, remotely, or — a consultation by video — online. */
export function placeKeyOf(session: BookingSession): string {
  if (session.kind === 'consultation' && (session.isVideoCall || session.isRemote)) {
    return 'web:booking.place.online';
  }
  return session.isRemote ? 'web:booking.place.remote' : 'web:booking.place.onSite';
}

/** The detail panel's reading of one session at `nowMs`. */
export function sessionDetail(session: BookingSession, nowMs: number): SessionDetail {
  const deadline = parseInstant(session.cancelFreeUntil);
  const open = session.status === 'scheduled' && !awaitsVerdict(session, nowMs) && !isLiveAt(session, nowMs);
  return {
    badge: badgeOf(session, nowMs),
    kindKey: kindKeyOf(session),
    placeKey: placeKeyOf(session),
    clientBooked: session.author === 'client',
    cancelFree: open && deadline !== null ? { ms: deadline, passed: deadline <= nowMs } : null,
    lateCancelFreeUntil: session.status === 'lateCancelled' ? deadline : null,
  };
}
